import { ENROLL_STATES } from '../constants';
import { WALMART_BENEFIT_IDS } from './constants';

const walmartBenefitIdsSet = new Set(WALMART_BENEFIT_IDS);

export const groupTrackersBySorBenefitId = (trackers) => {
  if (!Array.isArray(trackers)) return {};
  return trackers.reduce((acc, tracker) => {
    const { sorBenefitId } = tracker;
    if (!acc[sorBenefitId]) {
      acc[sorBenefitId] = [];
    }
    acc[sorBenefitId].push(tracker);
    return acc;
  }, {});
};

// Helper to merge trackers by sorBenefitId with metaContent
export const mergeTrackers = (trackersArr) => {
  if (!Array.isArray(trackersArr)) return [];
  // Separate trackers with and without metaContent
  const withMetaContent = trackersArr.filter((tracker) => tracker?.metaContent);
  const withoutMetaContent = trackersArr.filter((tracker) => !tracker?.metaContent);

  // Group withMetaContent by sorBenefitId
  const grouped = groupTrackersBySorBenefitId(withMetaContent);

  // Sort each group by metaContent.order ascending
  const sortedGroups = Object.values(grouped)
    .map((group) => group.sort((a, b) => (a.metaContent?.order ?? 0) - (b.metaContent?.order ?? 0))
    );

  // Output: array with both objects (no metaContent) and arrays (grouped/sorted with metaContent)
  return [...withoutMetaContent, ...sortedGroups];
};

export const getTrackersDataForDisplay = (data) => {
  const result = {
    spendProgress: [],
    creditEarned: [],
    completedBenefits: [],
  };

  if (!data || !Array.isArray(data)) {
    return result;
  }

  const trackers = data?.[0]?.trackers || [];

  // Use mergedTrackers for filtering
  const mergedTrackers = mergeTrackers(trackers);

  // completedBenefits: all trackers with status === "ACHIEVED"
  // with dual tracker, both trackers must be "ACHIEVED"
  result.completedBenefits = mergedTrackers.filter((tracker) => {
    if (Array.isArray(tracker)) {
      return tracker.every((element) => element?.status === 'ACHIEVED');
    } if (tracker && typeof tracker === 'object') {
      return tracker?.status === 'ACHIEVED';
    }
    return false;
  });

  // spendProgress: all trackers with category === "spend" and status === "IN_PROGRESS"
  // with dual tracker, one of the trackers can be "ACHIEVED"
  result.spendProgress = mergedTrackers.filter((tracker) => {
    if (Array.isArray(tracker)) {
      return tracker
        .some((element) => element?.category === 'spend' && element?.status === 'IN_PROGRESS');
    } if (typeof tracker === 'object') {
      return tracker?.category === 'spend' && tracker?.status === 'IN_PROGRESS';
    }
    return false;
  });

  // Collect sorBenefitIds already represented in spendProgress meta tracker groups
  // to avoid displaying them again individually in creditEarned
  const spendMetaSorBenefitIds = new Set(
    result.spendProgress
      .filter(Array.isArray)
      .flatMap((group) => group.map((t) => t.sorBenefitId))
  );

  // creditEarned: trackers with category === "usage" and status !== "ACHIEVED"
  // with dual tracker, one of the trackers can be "usage" and not achieved
  // excludes trackers already part of a spendProgress meta group
  result.creditEarned = trackers.filter((tracker) => {
    if (Array.isArray(tracker)) {
      return tracker.some((element) => element?.category === 'usage' && element?.status !== 'ACHIEVED');
    } if (typeof tracker === 'object') {
      if (spendMetaSorBenefitIds.has(tracker?.sorBenefitId)) return false;
      return tracker?.category === 'usage' && tracker?.status !== 'ACHIEVED';
    }
    return false;
  });

  return result;
};

export const fetchBenefitObject = (benefitsObj, sorBenefitId) => {
  if (!sorBenefitId) return null;
  return Object.values(benefitsObj).find(
    (benefit) => benefit.sorBenefitId === sorBenefitId
  ) || null;
};

export const isWalmartBenefitId = (sorBenefitId) => walmartBenefitIdsSet.has(sorBenefitId);

export const getInternalBenefits = (benefitsForActivity) => (benefitsForActivity
  ? Object.values(benefitsForActivity).filter(
    (b) => b?.enrollmentOriginType !== 'EXTERNAL'
      && b?.enrollmentOriginType !== 'NEITHER'
      && b?.enrollmentApplication?.enrollmentStatus !== 'ENROLLED'
  )
  : []);

export const getExternalBenefits = (viewAllBenefits) => (viewAllBenefits
  ? Object.values(viewAllBenefits).filter(
    (b) => b?.enrollmentOriginType === 'EXTERNAL'
      && b?.enrollmentOriginType !== 'NEITHER'
  )
  : []);

export const getBenefitsDataForQuickView = (viewAllBenefits, benefitTrackersData) => {
  if (!viewAllBenefits || !Array.isArray(benefitTrackersData)) return {};
  const sorBenefitIdsSet = new Set(benefitTrackersData.map((item) => item.sorBenefitId));
  return Object.values(viewAllBenefits)
    .filter((obj) => 'sorBenefitId' in obj && sorBenefitIdsSet.has(obj.sorBenefitId))
    .reduce((acc, obj) => {
      if (obj.benefitName) {
        acc[obj.benefitName] = obj;
      }
      return acc;
    }, {});
};

export const getBenefitNamesListFromAllBenefits = (benefitsDataForQuickView) => Object
  .values(benefitsDataForQuickView)
  .filter((obj) => obj.benefitName)
  .map((obj) => obj.benefitName);

export const decodeHtmlEntities = (html) => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = html;
  const decodedText = textArea.value;
  return decodedText.replace(/<\/?[^>]+(>|$)/g, '');
};

export const getEnrollStatus = (benefit) => {
  const descriptionKey = ENROLL_STATES
    .find((state) => benefit?.summary?.layout?.[state]?.showStatus?.description);
  const includeKey = ENROLL_STATES
    .find((state) => benefit?.summary?.layout?.[state]?.showStatus?.include);
  const showFlag = ENROLL_STATES
    .find((state) => benefit?.summary?.layout?.[state]?.showStatus?.show);

  const { description, include } = benefit?.summary?.layout?.[descriptionKey]?.showStatus || {};
  return `${description}${showFlag && includeKey ? ` ${include}` : ''}`;
};

export const mapMetaIcon = (detail, trackerStatus, order) => {
  const defaultProgressIcon = order === 1 ? 'IconRewards' : 'IconDollar';
  switch (trackerStatus) {
    case 'ACHIEVED':
      return detail?.achieved?.dlsicon?.length > 0 ? detail?.achieved?.dlsicon : 'IconRewards';

    case 'IN_PROGRESS':
      return detail?.progress?.dlsicon?.length > 0
        ? detail?.progress?.dlsicon : defaultProgressIcon;

    case 'UNKNOWN':
      return 'IconNeutral';

    default:
      return 'IconRewards';
  }
};

export const getEnrollmentStatusForColor = (benefit) => {
  const status = ENROLL_STATES
    .find((state) => benefit?.summary?.layout?.[state]?.showStatus?.description);
  switch (status) {
    case 'FAILED':
    case 'REJECTED':
      return 'warning';

    case 'ENROLLED':
      return 'success';

    case 'PENDING':
      return 'attention';

    case 'UNKNOWN':
      return 'neutral';

    default:
      return 'information';
  }
};
